import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

/**
 * Metric tile used on Managers overview and in the manager detail dialog.
 * When `subtitle` is passed (including `""`), layout is three rows: title → subtitle → value.
 * When `subtitle` is omitted, layout stays title → value (detail dialog).
 * @param {{ compact?: boolean }} props — `compact` uses a shorter tile (e.g. detail dialog).
 */
export function ManagerOverviewStatCard({ theme, title, value, subtitle, icon: Icon, compact = false }) {
  const threeLine = subtitle !== undefined;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: "100%",
        width: "100%",
        minHeight: compact ? { xs: "auto", md: threeLine ? 108 : 92 } : { xs: "auto", md: threeLine ? 132 : 118 },
        borderColor: theme.palette.divider,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <CardContent
        sx={{
          py: compact ? 1.25 : 2,
          height: "100%",
          "&:last-child": { pb: compact ? 1.25 : 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: compact ? 1 : 1.5,
            height: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.04em" }}>
              {title}
            </Typography>
            {threeLine ? (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    mt: compact ? 0.35 : 0.45,
                    lineHeight: 1.35,
                  }}
                >
                  {subtitle}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mt: compact ? 0.35 : 0.5,
                    color: theme.palette.text.primary,
                    wordBreak: "break-word",
                    lineHeight: 1.2,
                    fontVariantNumeric: "tabular-nums",
                    ...(compact ? { fontSize: "1.1rem" } : {}),
                  }}
                >
                  {value}
                </Typography>
              </>
            ) : (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mt: compact ? 0.35 : 0.5,
                  color: theme.palette.text.primary,
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                  fontVariantNumeric: "tabular-nums",
                  ...(compact ? { fontSize: "1.1rem" } : {}),
                }}
              >
                {value}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              p: compact ? 0.65 : 1,
              borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: compact ? 19 : 22 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
